import React, { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import DealerHeader from "../components/DealerHeader";

const ZONES = ["1", "2", "3", "3bis", "4"];

const defaultRules = {
  furtoIncendioTotale: {
    name: "Furto / Incendio Totale",
    emoji: "🔴",
    appliesTo: ["Base", "Medium", "Collisione", "Kasko"],
    limit: null,
    rules: {
      "1": { scoperto: "0%", franchigia: "€0" },
      "2": { scoperto: "0%", franchigia: "€0" },
      "3": { scoperto: "0%", franchigia: "€0" },
      "3bis": { scoperto: "0%", franchigia: "€0" },
      "4": { scoperto: "10%", franchigia: "€500" },
    },
  },
  furtoParziale: {
    name: "Furto Parziale",
    emoji: "🔴",
    appliesTo: ["Base", "Medium", "Collisione", "Kasko"],
    limit: null,
    rules: {
      "1": { scoperto: "10%", franchigia: "€250" },
      "2": { scoperto: "10%", franchigia: "€250" },
      "3": { scoperto: "10%", franchigia: "€250" },
      "3bis": { scoperto: "10%", franchigia: "€250" },
      "4": { scoperto: "15%", franchigia: "€500" },
    },
  },
  eventiNaturaliVandalismo: {
    name: "Eventi Naturali / Vandalismo",
    emoji: "🟠",
    appliesTo: ["Medium", "Collisione", "Kasko"],
    limit: "€6.000 per sinistro",
    rules: {
      "1": { scoperto: "10%", franchigia: "€250" },
      "2": { scoperto: "10%", franchigia: "€250" },
      "3": { scoperto: "15%", franchigia: "€350" },
      "3bis": { scoperto: "15%", franchigia: "€350" },
      "4": { scoperto: "15%", franchigia: "€500" },
    },
  },
  animaliSelvatici: {
    name: "Animali Selvatici",
    emoji: "🟡",
    appliesTo: ["Medium", "Collisione", "Kasko"],
    limit: "€3.000 / anno",
    rules: {
      "1": { scoperto: "10%", franchigia: "€250" },
      "2": { scoperto: "10%", franchigia: "€250" },
      "3": { scoperto: "10%", franchigia: "€250" },
      "3bis": { scoperto: "10%", franchigia: "€250" },
      "4": { scoperto: "15%", franchigia: "€500" },
    },
  },
  collisione: {
    name: "Collisione",
    emoji: "🔵",
    appliesTo: ["Collisione"],
    limit: null,
    rules: {
      "1": { scoperto: "10%", franchigia: "€600" },
      "2": { scoperto: "10%", franchigia: "€600" },
      "3": { scoperto: "10%", franchigia: "€600" },
      "3bis": { scoperto: "10%", franchigia: "€600" },
      "4": { scoperto: "15%", franchigia: "€600" },
    },
  },
  kasko: {
    name: "Kasko",
    emoji: "🟣",
    appliesTo: ["Kasko"],
    limit: null,
    rules: {
      "1": { scoperto: "10%", franchigia: "€750" },
      "2": { scoperto: "10%", franchigia: "€750" },
      "3": { scoperto: "10%", franchigia: "€750" },
      "3bis": { scoperto: "10%", franchigia: "€750" },
      "4": { scoperto: "15%", franchigia: "€1.250" },
    },
  },
};

export default function AdminConditions() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [rules, setRules] = useState(() => JSON.parse(JSON.stringify(defaultRules)));
  const [toastOpen, setToastOpen] = useState(false);

  const handleLimitChange = (ruleKey, value) => {
    setRules((prev) => ({
      ...prev,
      [ruleKey]: {
        ...prev[ruleKey],
        limit: value,
      },
    }));
  };

  const handleRuleChange = (ruleKey, zone, field, value) => {
    setRules((prev) => ({
      ...prev,
      [ruleKey]: {
        ...prev[ruleKey],
        rules: {
          ...prev[ruleKey].rules,
          [zone]: {
            ...prev[ruleKey].rules[zone],
            [field]: value,
          },
        },
      },
    }));
  };

  return (
    <Box sx={{ backgroundColor: "background.default", minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ pt: 3.5 }}>
        <DealerHeader
          showTabs={false}
          actionLabel="Torna ai Preventivi"
          onActionClick={() => navigate("/pages/dealer")}
        />
      </Container>

      <Container maxWidth="xl" sx={{ py: 3.5 }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Gestione Scoperti e Franchigie
          </Typography>
          <Button variant="contained" color="success" onClick={() => setToastOpen(true)}>
            Salva Modifiche
          </Button>
        </Box>

        <Box sx={{ display: "grid", gap: 2.5 }}>
          {Object.entries(rules).map(([ruleKey, rule]) => (
            <Card
              key={ruleKey}
              sx={{
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardHeader
                avatar={
                  <Avatar
                    sx={{
                      bgcolor: "action.selected",
                      color: "text.primary",
                    }}
                  >
                    {rule.emoji}
                  </Avatar>
                }
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="h6">{rule.name}</Typography>
                    {rule.appliesTo.map((pkg) => (
                      <Chip key={pkg} label={pkg} size="small" variant="outlined" />
                    ))}
                  </Box>
                }
                action={
                  rule.limit !== null ? (
                    <TextField
                      size="small"
                      label="Limite"
                      value={rule.limit || ""}
                      onChange={(event) => handleLimitChange(ruleKey, event.target.value)}
                      sx={{ minWidth: 220 }}
                    />
                  ) : null
                }
              />
              <Divider />
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, pl: 3 }}>Zona</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Scoperto
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Franchigia
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ZONES.map((zone, index) => (
                        <TableRow
                          key={zone}
                          sx={{
                            bgcolor:
                              index % 2 === 0
                                ? "background.paper"
                                : theme.palette.mode === "dark"
                                  ? alpha(theme.palette.primary.light, 0.12)
                                  : alpha(theme.palette.common.black, 0.03),
                          }}
                        >
                          <TableCell sx={{ pl: 3 }}>
                            <Chip label={`Zona ${zone}`} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              value={rule.rules[zone]?.scoperto || ""}
                              onChange={(event) =>
                                handleRuleChange(ruleKey, zone, "scoperto", event.target.value)
                              }
                              sx={{ width: 128 }}
                              inputProps={{ style: { textAlign: "center" } }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              value={rule.rules[zone]?.franchigia || ""}
                              onChange={(event) =>
                                handleRuleChange(ruleKey, zone, "franchigia", event.target.value)
                              }
                              sx={{ width: 128 }}
                              inputProps={{ style: { textAlign: "center" } }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>

      <Snackbar
        open={toastOpen}
        autoHideDuration={2600}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setToastOpen(false)} severity="success" variant="filled">
          Modifiche salvate con successo
        </Alert>
      </Snackbar>
    </Box>
  );
}
